import React, {
  FC,
  memo,
  useCallback,
}                          from 'react';
import styled, {
  Interpolation,
}                          from 'styled-components';
import { FieldInputProps } from 'formik';
import {
  Clear,
  Rotate90DegreesCcw,
  InsertPhoto,
}                          from '@material-ui/icons';
import {
  IPhoto,
  TRotationDegree,
}                          from '@typings/auction';
import { EPhotoType }      from '@enums/auction';
import { UploadButton }    from './UploadButton';

interface IStyledProps {
  top?      : number;
  left?     : number;
  right?    : number;
  bottom?   : number;
  rotation? : TRotationDegree;
}

const Wrapper = styled.div`
  height    : 400px;
  min-width : 100%;

  @media (min-width: 768px) {
    min-width: 400px;
  }

  > p {
    font-size     : 16px;
    margin-bottom : 10px;

    @media (max-width: 960px) {
      margin    : auto;
      max-width : 400px;
    }
  }

  > div:last-child {
    justify-content: center;
    max-width       : 400px;
    margin          : 0 auto;

    /* @media (min-width: 768px) {
      justify-content: flex-end;
    } */
  }
`;

const ImageContainer = styled.div<IStyledProps>`
  display             : flex;
  position            : relative;
  height              : 100%;
  padding             : 10px;
  border              : 1px solid black;
  background-position : center;
  background-size     : contain;
  background-repeat   : no-repeat;
  justify-content     : center;
  align-items         : center;
  text-align          : center;
  max-width           : 400px;
  max-height          : 300px;
  overflow            : hidden;

  @media (max-width: 960px) {
    margin: auto;
  }

  img {
    width     : 100%;
    max-width : 400px;

    ${({ rotation }: IStyledProps): string =>
    (rotation === 90 || rotation === 270
      ? `transform: rotate(${rotation}deg) scale(0.8)`
      : `transform: rotate(${rotation}deg)`
    )};
  }
`;

const IconContainer = styled.button<IStyledProps>`
   position   : absolute;
   float      : right;
   height     : 24px;
   cursor     : pointer;
   background : #707070;
   z-index    : 10;

   color: ${({ theme: { textColor } }): string => textColor.white};

   top    : ${({ top }    : IStyledProps): Interpolation<IStyledProps> => top}px;
   left   : ${({ left }   : IStyledProps): Interpolation<IStyledProps> => left}px;
   right  : ${({ right }  : IStyledProps): Interpolation<IStyledProps> => right}px;
   bottom : ${({ bottom } : IStyledProps): Interpolation<IStyledProps> => bottom}px;

  &:disabled {
    cursor: default;
  }
 `;

const BottomContainer = styled.div`
  width           : 100%;
  display         : flex;
  justify-content : center;

   @media(min-width: 960px) {
     justify-content : flex-end;
   }
`;

const getPhotoUrl = (photo: IPhoto): string =>
  (photo?.storageUrl
    ? `${photo.storageUrl}?${performance.now()}`
    : '');

interface IProps {
  title             : string;
  photo             : IPhoto;
  placeholderImage  : JSX.Element;
  field             : FieldInputProps<IPhoto>;
  photoType         : EPhotoType;
  isButtonsDisabled : boolean;
  isRemoveDisabled  : boolean;
  handleUploadPhoto : (
    photoType : EPhotoType,
    image     : File,
  ) => Promise<void>;
  handleRotatePhoto : (
    photoId : string,
    degree  : TRotationDegree,
  ) => Promise<void>;
  handleRemovePhoto : (photoId: string) => Promise<void>;
}

const PhotoItemComponent: FC<IProps> = ({
  field: { name },
  photo,
  title,
  photoType,
  placeholderImage,
  handleUploadPhoto,
  handleRotatePhoto,
  handleRemovePhoto,
  isButtonsDisabled,
  isRemoveDisabled,
}) => {
  const onUpload = useCallback(
    async (file: File): Promise<void> => {
      await handleUploadPhoto(
        photoType,
        file,
      );
    },
    [handleUploadPhoto, photoType],
  );

  return (
    <Wrapper>
      <p>{title}</p>

      <ImageContainer rotation={photo?.rotationDeg ?? 0}>
        {photo && (
          <IconContainer
            top      = {10}
            right    = {10}
            onClick  = {() => handleRemovePhoto(photo.id)}
            disabled = {isButtonsDisabled || isRemoveDisabled}
          >
            <Clear />
          </IconContainer>
        )}

        {photo?.storageUrl
          ? (
            <img
              src = {getPhotoUrl(photo)}
              alt = {title}
            />
          )
          : placeholderImage as JSX.Element}

        {photo && (
          <IconContainer
            bottom   = {10}
            right    = {10}
            onClick  = {() => handleRotatePhoto(photo.id, photo.rotationDeg)}
            disabled = {isButtonsDisabled}
          >
            <Rotate90DegreesCcw />
          </IconContainer>
        )}
      </ImageContainer>

      {handleUploadPhoto && (
        <BottomContainer>
          <UploadButton
            id       = {`photo-upload-${name}`}
            margin   = "10px 0 0"
            onUpload = {onUpload}
            disabled = {isButtonsDisabled}
          >
            <InsertPhoto />
            Upload Photo
          </UploadButton>
        </BottomContainer>
      )}
    </Wrapper>
  );
};

export const PhotoItem = memo(PhotoItemComponent);
